/* @jsx h */
import { defineComponent, h } from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import { CARD_PANEL_HEIGHT } from "./cardChrome";

/**
 * A place that holds more than one card, collapsed to a single pass in the deck.
 *
 * This is what makes the wallet one card per place: a restaurant running a stamp
 * card and a membership (or several) shows as one pass here — a short stack you
 * tap to fan open — instead of three near-identical cards you swipe past. It
 * wears the same chrome as a real pass (photo, scrim, fixed footer) so it reads
 * as a card in the deck, not a folder that wandered in; the giveaway is the
 * fanned corners behind it and the count.
 */

export type PlaceStackCardProps = {
  name: string;
  /** The shop's pass poster image, if it has one. */
  imageUrl?: string | null;
  /** Material-symbol glyph for the place — borrowed from its first card. */
  icon: string;
  /** The names of the cards inside, shown so the place is legible unopened. */
  labels: string[];
  /** Drop the width cap and fill the container — the stack owns its own width. */
  wide?: boolean;
  /** The event is passed so the caller can read the tapped card's rect, which
   * the fan grows out of. */
  onSelect?: (e?: Event) => void;
};

export const PlaceStackCard = defineComponent<PlaceStackCardProps>({
  name: "PlaceStackCard",
  props: ["name", "imageUrl", "icon", "labels", "wide", "onSelect"],
  setup(props) {
    const { t } = useLocalization();

    return () => {
      const hasImage = !!props.imageUrl;
      const count = props.labels.length;
      const interactive = !!props.onSelect;

      return (
        <div
          role={interactive ? "button" : undefined}
          tabindex={interactive ? 0 : undefined}
          onClick={(e: Event) => props.onSelect?.(e)}
          onKeydown={(e: KeyboardEvent) => {
            if (interactive && (e.key === "Enter" || e.key === " ")) {
              e.preventDefault();
              props.onSelect?.(e);
            }
          }}
          class={[
            hasImage ? "text-white" : "bg-base-100 text-base-content",
            "relative overflow-hidden rounded-3xl shadow-xl select-none",
            props.wide
              ? "w-full h-full"
              : "aspect-[5/7] w-full max-w-[20rem] mx-auto",
            interactive
              ? "cursor-pointer transition-transform duration-200 hover:-translate-y-1 focus:-translate-y-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-current"
              : "",
          ]}
        >
          {hasImage && (
            <img
              src={props.imageUrl!}
              alt=""
              aria-hidden="true"
              class="absolute inset-0 w-full h-full object-cover"
            />
          )}
          <div
            aria-hidden="true"
            class={
              hasImage
                ? "absolute inset-0 bg-gradient-to-b from-black/55 via-black/25 to-black/70"
                : "absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-base-300/40"
            }
          />

          <div class="relative h-full flex flex-col">
            <div class="flex items-start justify-between gap-3 p-5">
              <div class="flex items-center gap-2 min-w-0">
                <span class="material-symbols-outlined text-xl shrink-0 drop-shadow">
                  {props.icon}
                </span>
                <p class="font-mono text-[11px] uppercase tracking-[0.2em] truncate drop-shadow">
                  {props.name}
                </p>
              </div>
            </div>

            {/* The count over a pair of fanned corners — the visual promise that
                there is a small stack here to deal open. */}
            <div class="flex-1 flex items-center justify-center min-h-0 relative">
              <div
                aria-hidden="true"
                class={`absolute w-32 h-40 rounded-2xl border ${
                  hasImage
                    ? "border-white/25 bg-white/10"
                    : "border-current/20 bg-current/5"
                }`}
                style={{ transform: "translateY(14px) rotate(6deg) scale(0.92)" }}
              />
              <div
                aria-hidden="true"
                class={`absolute w-32 h-40 rounded-2xl border ${
                  hasImage
                    ? "border-white/30 bg-white/10"
                    : "border-current/25 bg-current/5"
                }`}
                style={{ transform: "translateY(7px) rotate(-4deg) scale(0.96)" }}
              />
              <div class="relative text-center leading-none">
                <p class="font-mono text-5xl font-bold drop-shadow tabular-nums">
                  {count}
                </p>
                <p class="font-mono text-[11px] uppercase tracking-[0.16em] opacity-85 mt-1 drop-shadow">
                  {t("customer.collected.cards_count")}
                </p>
              </div>
            </div>

            <div
              class={`mt-auto p-5 flex flex-col justify-between ${CARD_PANEL_HEIGHT} ${
                hasImage
                  ? "bg-black/55 border-t border-white/15"
                  : "bg-base-200 border-t border-current/15"
              }`}
            >
              <div class="space-y-1.5 min-h-0 overflow-hidden">
                {props.labels.slice(0, 3).map((label, i) => (
                  <div key={i} class="flex items-center gap-2">
                    <span
                      class={`w-1.5 h-1.5 rounded-full shrink-0 ${
                        hasImage ? "bg-white/70" : "bg-current/50"
                      }`}
                    />
                    <p class="font-mono text-xs truncate">{label}</p>
                  </div>
                ))}
                {count > 3 && (
                  <p class="font-mono text-[11px] opacity-70 pl-3.5">
                    {t("customer.collected.plus_more", [count - 3])}
                  </p>
                )}
              </div>
              <p class="font-mono text-[11px] uppercase tracking-[0.12em] opacity-80 flex items-center gap-1">
                <span class="material-symbols-outlined text-sm">touch_app</span>
                {t("customer.collected.tap_to_open")}
              </p>
            </div>
          </div>
        </div>
      );
    };
  },
});
